import {
	FormattedDate,
	IDataListColumn,
	IconButton,
	PaginatedAutoDataList,
	PaginatedListFetcherConfig,
	laravelPaginatedListFetchConfig,
	useEditDrawer,
} from "@dgs/core";
import { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFilterQuery } from "../../../shared/api/filterQueries";
import { useFilter } from "../../../shared/filters";
import { FilterBar } from "../../../shared/filters/FilterBar";
import { FilterDrawer } from "../../../shared/filters/FilterDrawer";
import { applicationService } from "../shared/api/applicationService";
import { ApplicationIndexResource, ApplicationType } from "../shared/types";
import { ApplicationContextMenu } from "./ApplicationContextMenu";
import { CreateApplicationAction } from "./CreateApplicationAction";
import { EditApplicationDrawer } from "./EditApplicationDrawer";

export const ApplicationList: FC = () => {
	const { t } = useTranslation();
	const [applicationType, setApplicationType] = useState<ApplicationType | null>(null);
	const { editEntityId, handleOpen, ...editProps } = useEditDrawer();
	const { activeFilterId, resetFilter, applyFilter } = useFilter();
	const filterDrawerProps = useEditDrawer();
	const { data: predefinedFilter } = useFilterQuery({ filterId: activeFilterId });
	const openFilterDrawer = useCallback(() => {
		filterDrawerProps.handleOpen(activeFilterId || "new");
	}, [activeFilterId, filterDrawerProps]);

	const columns: IDataListColumn<ApplicationIndexResource>[] = [
		{
			type: "value",
			valueKey: "type",
			heading: t("Type"),
			render: (application) => t(application.type),
		},
		{
			type: "value",
			valueKey: "projectNumber",
			heading: t("Project number"),
		},
		{
			type: "value",
			valueKey: "subdomain",
			heading: t("Subdomain"),
		},
		{
			type: "value",
			valueKey: "setupDate",
			heading: t("Setup date"),
			render: (application) => <FormattedDate date={application.setupDate} />,
		},
		{
			type: "value",
			valueKey: "deletionDate",
			heading: t("Deletion date"),
			render: (application) => <FormattedDate date={application.deletionDate} />,
		},
		{
			type: "actions",
			heading: "",
			size: "max-content",
			action: (application) => (
				<ApplicationContextMenu
					application={application}
					handleEdit={() => {
						setApplicationType(application.type);
						handleOpen(application.id);
					}}
				/>
			),
		},
	];

	return (
		<>
			<PaginatedAutoDataList
				name="applications"
				heading={t("Applications")}
				headerActions={
					<>
						<CreateApplicationAction />
						<IconButton title={t("Open filter")} onClick={openFilterDrawer} size="small" icon="filter" />
					</>
				}
				headerChildren={<FilterBar filterId={activeFilterId} onRemove={resetFilter} onClick={openFilterDrawer} />}
				initialSearch={{
					search: "",
					searchFields: [
						{ name: "projectNumber", label: t("projectNumber") },
						{ name: "subdomain", label: t("Subdomain") },
					],
				}}
				empty={t("No applications")}
				columns={columns}
				listFetchConfig={laravelPaginatedListFetchConfig(
					[...applicationService.keys.list, `${activeFilterId}`],
					(config: PaginatedListFetcherConfig<ApplicationIndexResource>) =>
						applicationService.index(config, activeFilterId),
				)}
			/>
			{applicationType && editEntityId && (
				<EditApplicationDrawer entityId={editEntityId} applicationType={applicationType} {...editProps} />
			)}
			{filterDrawerProps.editEntityId && (
				<FilterDrawer {...filterDrawerProps} filter={predefinedFilter || null} onSubmit={applyFilter} />
			)}
		</>
	);
};
